import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  CalendarRange,
  CheckCircle2,
  LineChart,
  Loader2,
  Lock,
  PackagePlus,
  Send,
  ShieldCheck,
  Sparkles,
  Star,
  Wallet,
  X,
} from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Money } from "@/components/ui/riyal";

export const Route = createFileRoute("/partner")({
  head: () => ({
    meta: [
      { title: "Become a Partner — Spayzo" },
      { name: "description", content: "List your space on Spayzo and start earning. Manage bookings, schedules, and revenue from one verified, secure dashboard." },
      { property: "og:title", content: "Become a Partner — Spayzo" },
      { property: "og:description", content: "Join thousands of verified hosts earning monthly income with Spayzo's space booking platform." },
    ],
  }),
  component: PartnerPage,
});

function PartnerPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        <Hero />
        <Benefits />
        <HowItWorks />
        <Trust />
        <Onboarding />
        <Testimonials />
        <FinalCTA />
      </main>
      <SiteFooter />
    </div>
  );
}

function Hero() {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-primary-soft/60 via-background to-background" />
      <div
        className="absolute inset-x-0 top-0 -z-10 h-[420px] opacity-60"
        style={{ background: "radial-gradient(60% 60% at 50% 0%, oklch(0.85 0.12 262 / 0.5), transparent 70%)" }}
      />

      <div className="mx-auto grid max-w-7xl gap-12 px-4 pt-16 pb-20 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:pt-24 lg:pb-28">
        <div className="flex flex-col justify-center">
          <span className="inline-flex w-fit items-center gap-2 rounded-full border border-primary/20 bg-card px-3 py-1 text-xs font-medium text-primary shadow-soft">
            <Sparkles className="h-3.5 w-3.5" />
            {t("partner.badge")}
          </span>

          <h1 className="mt-5 text-balance font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            {t("partner.titleA")}{" "}
            <span className="bg-gradient-to-r from-primary to-[oklch(0.65_0.2_290)] bg-clip-text text-transparent">
              {t("partner.titleB")}
            </span>
          </h1>

          <p className="mt-5 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t("partner.subtitle")}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="#onboarding"
              className="inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-sm font-semibold text-background shadow-elevated transition-transform hover:scale-[1.02]"
            >
              {t("partner.cta")} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
            </a>
            <a
              href="#how"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-6 py-3 text-sm font-medium text-foreground shadow-soft transition-colors hover:bg-secondary"
            >
              {t("partner.howLink")}
            </a>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-6 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              {t("partner.verifiedHosts")}
            </div>
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-primary" />
              {t("partner.payouts24")}
            </div>
            <div className="flex items-center gap-2">
              <Star className="h-4 w-4 text-primary" />
              {t("partner.hostRating")}
            </div>
          </div>
        </div>

        <DashboardPreview />
      </div>
    </section>
  );
}

function DashboardPreview() {
  const { t } = useTranslation();
  const stats = [
    { key: "earnings", value: <Money value="3,840" />, trend: "+24%" },
    { key: "bookings", value: "47", trend: "+12%" },
    { key: "rating", value: "4.9★", trend: "+0.2" },
  ] as const;
  const items = [
    { name: "Studio Loft · 14:00", status: "confirmed" as const },
    { name: "Rooftop Terrace · 18:30", status: "pending" as const },
  ];
  return (
    <div className="relative">
      <div className="absolute -inset-6 -z-10 rounded-[2.5rem] bg-gradient-to-br from-primary/15 to-[oklch(0.7_0.2_290)]/15 blur-2xl" />
      <div className="rounded-3xl border border-border bg-card p-3 shadow-elevated">
        <div className="flex items-center gap-1.5 px-3 py-2">
          <span className="h-2.5 w-2.5 rounded-full bg-destructive/60" />
          <span className="h-2.5 w-2.5 rounded-full bg-[oklch(0.85_0.15_90)]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[oklch(0.75_0.15_140)]" />
          <span className="ms-3 text-[11px] text-muted-foreground">partners.spayzo.app</span>
        </div>

        <div className="rounded-2xl bg-surface p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-medium text-muted-foreground">{t("partner.preview.welcome")}</div>
              <div className="mt-0.5 font-display text-lg font-bold">{t("partner.preview.overview")}</div>
            </div>
            <span className="rounded-full bg-[oklch(0.94_0.08_140)] px-2.5 py-1 text-[11px] font-semibold text-[oklch(0.4_0.15_140)]">
              {t("partner.preview.live")}
            </span>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-3">
            {stats.map((s) => (
              <div key={s.key} className="rounded-xl bg-card p-3 shadow-soft">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t(`partner.preview.${s.key}`)}</div>
                <div className="mt-1 font-display text-lg font-bold">{s.value}</div>
                <div className="mt-1 text-[10px] font-semibold text-[oklch(0.45_0.15_140)]">{s.trend}</div>
              </div>
            ))}
          </div>

          <div className="mt-4 rounded-xl bg-card p-4 shadow-soft">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium">{t("partner.preview.revenue7d")}</span>
              <span className="text-muted-foreground">{t("partner.preview.monToSun")}</span>
            </div>
            <div className="mt-3 flex h-24 items-end gap-2">
              {[40, 65, 50, 80, 55, 90, 72].map((h, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-t-md bg-gradient-to-t from-primary to-[oklch(0.7_0.2_290)]"
                  style={{ height: `${h}%` }}
                />
              ))}
            </div>
          </div>

          <div className="mt-4 space-y-2">
            {items.map((b) => (
              <div key={b.name} className="flex items-center justify-between rounded-xl bg-card px-4 py-3 shadow-soft">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-soft text-primary">
                    <CalendarRange className="h-4 w-4" />
                  </div>
                  <span className="text-xs font-medium">{b.name}</span>
                </div>
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[10px] font-semibold",
                    b.status === "confirmed"
                      ? "bg-[oklch(0.94_0.08_140)] text-[oklch(0.4_0.15_140)]"
                      : "bg-[oklch(0.95_0.08_90)] text-[oklch(0.45_0.15_70)]",
                  )}
                >
                  {t(`partner.preview.${b.status}`)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute -bottom-6 -start-6 hidden rounded-2xl border border-border bg-card p-4 shadow-elevated sm:block">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[oklch(0.94_0.08_140)] text-[oklch(0.4_0.15_140)]">
            <BadgeCheck className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xs font-semibold">{t("partner.preview.verifiedHost")}</div>
            <div className="text-[11px] text-muted-foreground">{t("partner.preview.activated")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

const benefits = [
  { icon: Wallet, key: "earn" },
  { icon: CalendarRange, key: "flex" },
  { icon: BadgeCheck, key: "manage" },
  { icon: LineChart, key: "analytics" },
  { icon: PackagePlus, key: "addons" },
  { icon: ShieldCheck, key: "secure" },
] as const;

function Benefits() {
  const { t } = useTranslation();
  return (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
      <div className="max-w-2xl">
        <span className="text-xs font-semibold uppercase tracking-widest text-primary">{t("partner.benefitsEyebrow")}</span>
        <h2 className="mt-3 text-balance font-display text-3xl font-bold tracking-tight sm:text-4xl">
          {t("partner.benefitsTitle")}
        </h2>
      </div>

      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {benefits.map((b) => (
          <div
            key={b.key}
            className="group rounded-2xl border border-border bg-card p-6 shadow-soft transition-all hover:-translate-y-1 hover:shadow-elevated"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-soft text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
              <b.icon className="h-5 w-5" />
            </div>
            <h3 className="mt-5 font-display text-lg font-semibold">{t(`partner.benefits.${b.key}.title`)}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t(`partner.benefits.${b.key}.desc`)}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const steps = [
  { n: "01", icon: Building2, key: "s1" },
  { n: "02", icon: CalendarRange, key: "s2" },
  { n: "03", icon: Wallet, key: "s3" },
] as const;

function HowItWorks() {
  const { t } = useTranslation();
  return (
    <section id="how" className="border-y border-border bg-surface py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-widest text-primary">{t("partner.howEyebrow")}</span>
          <h2 className="mt-3 text-balance font-display text-3xl font-bold tracking-tight sm:text-4xl">
            {t("partner.howTitle")}
          </h2>
        </div>

        <div className="relative mt-14 grid gap-8 md:grid-cols-3">
          <div className="absolute start-0 end-0 top-7 hidden h-px bg-gradient-to-r from-transparent via-border to-transparent md:block" />
          {steps.map((s) => (
            <div key={s.n} className="relative">
              <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-2xl border border-border bg-card font-display text-xl font-bold shadow-soft">
                {s.n}
              </div>
              <div className="mt-6 flex h-10 w-10 items-center justify-center rounded-xl bg-primary-soft text-primary">
                <s.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-xl font-semibold">{t(`partner.steps.${s.key}.title`)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t(`partner.steps.${s.key}.desc`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Trust() {
  const { t } = useTranslation();
  const list = t("partner.trustList", { returnObjects: true }) as string[];
  const cards = [
    { icon: BadgeCheck, key: "verified" },
    { icon: Lock, key: "secure" },
    { icon: ShieldCheck, key: "trusted" },
    { icon: Sparkles, key: "quality" },
  ] as const;
  return (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
      <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
        <div>
          <span className="text-xs font-semibold uppercase tracking-widest text-primary">{t("partner.trustEyebrow")}</span>
          <h2 className="mt-3 text-balance font-display text-3xl font-bold tracking-tight sm:text-4xl">
            {t("partner.trustTitle")}
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-muted-foreground">
            {t("partner.trustBody")}
          </p>

          <ul className="mt-8 space-y-4">
            {list.map((tx) => (
              <li key={tx} className="flex items-start gap-3 text-sm">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span>{tx}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {cards.map((c) => (
            <div key={c.key} className="rounded-2xl border border-border bg-card p-6 shadow-soft">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-soft text-primary">
                <c.icon className="h-5 w-5" />
              </div>
              <div className="mt-4 font-display text-base font-semibold">{t(`partner.trustCards.${c.key}.title`)}</div>
              <div className="mt-1 text-xs text-muted-foreground">{t(`partner.trustCards.${c.key}.desc`)}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const businessSchema = z.object({
  businessNameAr: z.string().trim().min(2, "Required"),
  businessNameEn: z.string().trim().min(2, "Required"),
  tradeName: z.string().trim().max(100).optional().or(z.literal("")),
  businessType: z.enum(["sole", "company"]).refine((v) => !!v, { message: "Select a type" }),
  city: z.string().min(1, "Select a city"),
  address: z.string().trim().min(5, "Please add a full address"),
  mapPin: z
    .string()
    .trim()
    .min(1, "Paste a Google Maps link")
    .url("Enter a valid URL")
    .refine((v) => /google\.[^/]+\/maps|maps\.app\.goo\.gl|goo\.gl\/maps/i.test(v), {
      message: "Must be a Google Maps link",
    }),
  email: z.string().trim().min(1, "Email is required").email("Enter a valid email").max(255),
  password: z
    .string()
    .min(8, "At least 8 characters")
    .max(72, "Too long")
    .regex(/[A-Z]/, "Include an uppercase letter")
    .regex(/[0-9]/, "Include a number"),
});

type FormState = {
  businessNameAr: string;
  businessNameEn: string;
  tradeName: string;
  businessType: "" | "sole" | "company";
  city: string;
  address: string;
  mapPin: string;
  email: string;
  password: string;
};

const initial: FormState = {
  businessNameAr: "",
  businessNameEn: "",
  tradeName: "",
  businessType: "",
  city: "",
  address: "",
  mapPin: "",
  email: "",
  password: "",
};

function Onboarding() {
  const { t } = useTranslation();
  const [data, setData] = useState<FormState>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = useCallback(<K extends keyof FormState>(k: K, v: FormState[K]) => {
    setData((d) => ({ ...d, [k]: v }));
    setErrors((e) => {
      if (!e[k as string]) return e;
      const { [k as string]: _omit, ...rest } = e;
      return rest;
    });
  }, []);

  const validate = (): boolean => {
    const r = businessSchema.safeParse(data);
    if (!r.success) {
      const errs: Record<string, string> = {};
      r.error.issues.forEach((i) => {
        if (i.path[0]) errs[String(i.path[0])] = i.message;
      });
      setErrors(errs);
      return false;
    }
    setErrors({});
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 1200));
    setSubmitting(false);
    setSubmitted(true);
  };

  if (submitted) return <SubmittedState />;

  return (
    <section id="onboarding" className="border-y border-border bg-surface py-20 lg:py-28">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-widest text-primary">{t("partner.onboardingEyebrow")}</span>
          <h2 className="mt-3 text-balance font-display text-3xl font-bold tracking-tight sm:text-4xl">
            {t("partner.onboardingTitle")}
          </h2>
          <p className="mt-3 text-sm text-muted-foreground">{t("partner.onboardingHint")}</p>
        </div>

        <div className="mt-6 rounded-3xl border border-border bg-card p-6 shadow-card sm:p-10">
          <BusinessStep data={data} errors={errors} update={update} />

          <div className="mt-10 flex justify-end">
            <Button type="button" onClick={submit} disabled={submitting} className="rounded-full">
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> {t("partner.submitting")}
                </>
              ) : (
                <>
                  {t("partner.submit")} <Send className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>

        <VerificationNotice />
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 p-3 shadow-elevated backdrop-blur md:hidden">
        <Button type="button" onClick={submit} disabled={submitting} className="h-12 w-full rounded-full text-sm">
          {submitting ? t("partner.submitting") : t("partner.submit")}
        </Button>
      </div>
    </section>
  );
}

function BusinessStep({
  data,
  errors,
  update,
}: {
  data: FormState;
  errors: Record<string, string>;
  update: <K extends keyof FormState>(k: K, v: FormState[K]) => void;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <SectionHeading index={t("partner.step01")} title={t("partner.businessInfo")} desc={t("partner.businessInfoDesc")} />
      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        <Field label={t("partner.fields.nameAr")} error={errors.businessNameAr} required>
          <Input
            dir="rtl"
            placeholder={t("partner.fields.nameArPh")}
            value={data.businessNameAr}
            onChange={(e) => update("businessNameAr", e.target.value)}
          />
        </Field>
        <Field label={t("partner.fields.nameEn")} error={errors.businessNameEn} required>
          <Input
            placeholder={t("partner.fields.nameEnPh")}
            value={data.businessNameEn}
            onChange={(e) => update("businessNameEn", e.target.value)}
          />
        </Field>

        <Field label={t("partner.fields.trade")}>
          <Input placeholder={t("partner.fields.tradePh")} value={data.tradeName} onChange={(e) => update("tradeName", e.target.value)} />
        </Field>
        <Field label={t("partner.fields.type")} error={errors.businessType} required>
          <Select
            value={data.businessType}
            onValueChange={(v) => update("businessType", v as FormState["businessType"])}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("partner.fields.typePh")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sole">{t("partner.fields.sole")}</SelectItem>
              <SelectItem value="company">{t("partner.fields.company")}</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <Field label={t("partner.fields.city")} error={errors.city} required>
          <Select value={data.city} onValueChange={(v) => update("city", v)}>
            <SelectTrigger>
              <SelectValue placeholder={t("partner.fields.cityPh")} />
            </SelectTrigger>
            <SelectContent>
              {["Riyadh", "Jeddah", "Dammam", "Khobar", "Mecca", "Medina"].map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label={t("partner.fields.address")} error={errors.address} required>
          <Input
            placeholder={t("partner.fields.addressPh")}
            value={data.address}
            onChange={(e) => update("address", e.target.value)}
          />
        </Field>

        <div className="sm:col-span-2">
          <Field label={t("partner.fields.map")} error={errors.mapPin} required>
            <Input
              type="url"
              inputMode="url"
              placeholder={t("partner.fields.mapPh")}
              value={data.mapPin}
              onChange={(e) => update("mapPin", e.target.value)}
            />
            <p className="mt-1.5 text-xs text-muted-foreground">{t("partner.fields.mapHelp")}</p>
          </Field>
        </div>

        <Field label={t("partner.fields.email")} error={errors.email} required>
          <Input
            type="email"
            autoComplete="email"
            placeholder={t("partner.fields.emailPh")}
            value={data.email}
            onChange={(e) => update("email", e.target.value)}
          />
        </Field>
        <Field label={t("partner.fields.password")} error={errors.password} required>
          <Input
            type="password"
            autoComplete="new-password"
            placeholder={t("partner.fields.passwordPh")}
            value={data.password}
            onChange={(e) => update("password", e.target.value)}
          />
        </Field>
      </div>
    </div>
  );
}

function VerificationNotice() {
  const { t } = useTranslation();
  return (
    <div className="mt-8 rounded-2xl border border-[oklch(0.85_0.12_90)]/40 bg-[oklch(0.97_0.05_90)] p-5 sm:p-6">
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[oklch(0.92_0.1_85)] text-[oklch(0.45_0.15_70)]">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div>
          <div className="font-display text-base font-semibold text-[oklch(0.35_0.12_70)]">
            {t("partner.notice.title")}
          </div>
          <p className="mt-2 text-sm leading-relaxed text-[oklch(0.4_0.08_70)]">
            <span className="font-medium">{t("partner.notice.body")}</span>
          </p>
        </div>
      </div>
    </div>
  );
}

function SubmittedState() {
  const { t } = useTranslation();
  return (
    <section id="onboarding" className="border-y border-border bg-surface py-24">
      <div className="mx-auto max-w-xl px-6 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[oklch(0.94_0.08_140)] text-[oklch(0.4_0.15_140)] shadow-soft">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h2 className="mt-6 font-display text-3xl font-bold tracking-tight">{t("partner.submitted.title")}</h2>
        <p className="mt-3 text-sm text-muted-foreground">{t("partner.submitted.body")}</p>
        <div className="mt-8 flex justify-center gap-3">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-3 text-sm font-semibold text-background"
          >
            {t("partner.submitted.dashboard")}
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-medium"
          >
            {t("partner.submitted.home")}
          </Link>
        </div>
      </div>
    </section>
  );
}

function SectionHeading({ index, title, desc }: { index: string; title: string; desc: string }) {
  return (
    <div className="flex items-start gap-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground font-display text-sm font-bold">
        {index}
      </div>
      <div>
        <h3 className="font-display text-xl font-semibold tracking-tight sm:text-2xl">{title}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  required,
  children,
}: {
  label: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="flex items-center gap-1">
        {label}
        {required && <span className="text-destructive">*</span>}
      </Label>
      {children}
      {error && (
        <p className="flex items-center gap-1 text-xs font-medium text-destructive">
          <X className="h-3 w-3" /> {error}
        </p>
      )}
    </div>
  );
}

const quotes = [
  { key: "maya", initials: "MA", rating: 5 },
  { key: "omar", initials: "OF", rating: 5 },
  { key: "lina", initials: "LH", rating: 5 },
] as const;

function Testimonials() {
  const { t } = useTranslation();
  return (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
      <div className="max-w-2xl">
        <span className="text-xs font-semibold uppercase tracking-widest text-primary">{t("partner.testEyebrow")}</span>
        <h2 className="mt-3 text-balance font-display text-3xl font-bold tracking-tight sm:text-4xl">
          {t("partner.testTitle")}
        </h2>
      </div>
      <div className="mt-12 grid gap-5 md:grid-cols-3">
        {quotes.map((q) => (
          <figure key={q.key} className="rounded-2xl border border-border bg-card p-6 shadow-soft">
            <div className="flex gap-0.5 text-[oklch(0.78_0.16_80)]">
              {Array.from({ length: q.rating }).map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-current" />
              ))}
            </div>
            <blockquote className="mt-4 text-sm leading-relaxed text-foreground">"{t(`partner.quotes.${q.key}.text`)}"</blockquote>
            <figcaption className="mt-5 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-soft text-xs font-semibold text-primary">
                {q.initials}
              </div>
              <div>
                <div className="text-sm font-semibold">{t(`partner.quotes.${q.key}.name`)}</div>
                <div className="text-xs text-muted-foreground">{t(`partner.quotes.${q.key}.role`)}</div>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

function FinalCTA() {
  const { t } = useTranslation();
  return (
    <section className="px-4 pb-24 sm:px-6 lg:px-8">
      <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] bg-gradient-to-br from-[oklch(0.18_0.02_260)] to-[oklch(0.25_0.08_260)] px-8 py-16 text-center text-white shadow-elevated sm:px-12 lg:rounded-[2.5rem] lg:py-20">
        <div
          className="absolute inset-0 -z-10 opacity-50"
          style={{ background: "radial-gradient(50% 80% at 50% 0%, oklch(0.65 0.2 280 / 0.4), transparent 70%)" }}
        />
        <h2 className="mx-auto max-w-2xl text-balance font-display text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
          {t("partner.finalTitle")}
        </h2>
        <p className="mx-auto mt-4 max-w-md text-base text-white/70">{t("partner.finalBody")}</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a
            href="#onboarding"
            className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-foreground transition-transform hover:scale-[1.02]"
          >
            {t("partner.finalCta")} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
          </a>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-medium text-white hover:bg-white/10"
          >
            {t("partner.contactSales")}
          </Link>
        </div>
      </div>
    </section>
  );
}
